import * as Popover from '@radix-ui/react-popover';
import type { AutomationCategoryResponse } from '@reach/shared-types/automation-category';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { CategoryChecklist } from '../CategoryChecklist/CategoryChecklist';
import { CategoryEditDialog } from '../CategoryEditDialog/CategoryEditDialog';
import { keepOpenWhileEditingCategory } from '../CategoryEditDialog/categoryEditDialogTarget';
import { CategoryPillList } from '../CategoryPillList/CategoryPillList';
import { useAutomationCategories } from '../queries';
import styles from './TemplateCategoriesCell.module.css';
import { useTemplateCategoryAssignment } from './useTemplateCategoryAssignment';

interface TemplateCategoriesCellProps {
  templateId: string;
  templateName: string;
  categories: AutomationCategoryResponse[];
}

/** A template's categories in the list, with a "+" (shown on row hover) to edit them in place. */
export function TemplateCategoriesCell({
  templateId,
  templateName,
  categories,
}: TemplateCategoriesCellProps) {
  const [categoryBeingEdited, setCategoryBeingEdited] = useState<AutomationCategoryResponse | null>(
    null,
  );
  const { categoryIds, assign } = useTemplateCategoryAssignment({
    templateId,
    savedCategoryIds: categories.map(category => category.id),
  });
  const { data: allCategories = [] } = useAutomationCategories();
  const displayedCategories = categoryIds
    .map(categoryId => allCategories.find(category => category.id === categoryId))
    .filter((category): category is AutomationCategoryResponse => category !== undefined);
  const editLabel = `Edit categories for ${templateName}`;

  return (
    <td className={styles.categoriesCell} onClick={event => event.stopPropagation()}>
      <CategoryPillList categories={displayedCategories} />
      <Popover.Root>
        <Popover.Trigger asChild>
          <button type="button" className={styles.addButton} aria-label={editLabel}>
            <Plus aria-hidden />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            className={styles.menu}
            align="start"
            sideOffset={6}
            onInteractOutside={keepOpenWhileEditingCategory}>
            <CategoryChecklist
              selectedCategoryIds={categoryIds}
              onSelectedCategoryIdsChange={assign}
              onEditCategory={setCategoryBeingEdited}
            />
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      {categoryBeingEdited && (
        <CategoryEditDialog
          category={categoryBeingEdited}
          onClose={() => setCategoryBeingEdited(null)}
        />
      )}
    </td>
  );
}
